/**
 * /tickets/:n/service-orders/:id/print (spec 016 FR-006) — página limpa, fora
 * do layout do app, que abre o diálogo de impressão do navegador assim que a
 * OS carrega. Uma folha: cabeçalho, serviço, recurso, endereço, contato,
 * instruções e campos em branco para o técnico preencher à mão.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  SERVICE_ORDER_PERIOD_LABELS,
  SERVICE_ORDER_STATUS_LABELS,
  SERVICE_ORDER_TYPE_LABELS,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { PageSpinner } from '@/components/ui/misc';
import { capitalize, useTerms } from '@/shared/tenant-profile';
import { formatIsoDate, serviceOrderErrorMessage, serviceOrderQuery } from './service-orders-queries';

export function ServiceOrderPrintPage() {
  const params = useParams({ strict: false }) as { ticketNumber?: string; id?: string };
  const ticketNumber = Number(params.ticketNumber ?? 0);
  const id = Number(params.id ?? 0);
  const osQ = useQuery(serviceOrderQuery(ticketNumber, id));
  // spec 017: contato e recurso no vocabulário do tenant
  const terms = useTerms();
  const printed = useRef(false);

  useEffect(() => {
    if (!osQ.data || printed.current) return;
    printed.current = true;
    // um frame para o layout assentar antes do diálogo
    requestAnimationFrame(() => window.print());
  }, [osQ.data]);

  if (osQ.isPending) return <PageSpinner />;
  if (osQ.isError) {
    return (
      <p className="p-8 text-sm text-destructive">
        Não foi possível carregar a OS: {serviceOrderErrorMessage(osQ.error)}
      </p>
    );
  }

  const os = osQ.data;
  const cityLine = [os.addressCity, os.addressState].filter(Boolean).join('/');
  return (
    <div className="mx-auto max-w-3xl bg-white p-8 text-[13px] leading-relaxed text-black print:p-0">
      <div className="mb-4 flex justify-end print:hidden">
        <Button size="sm" onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>

      <header className="flex items-start justify-between border-b-2 border-black pb-3">
        <div>
          <h1 className="text-xl font-bold">Ordem de serviço nº {os.number}</h1>
          <p>
            {SERVICE_ORDER_TYPE_LABELS[os.type]} · Ticket #{os.ticketNumber} ·{' '}
            {SERVICE_ORDER_STATUS_LABELS[os.status]}
          </p>
        </div>
        <div className="text-right">
          <p className="font-semibold">
            {os.scheduledDate ? formatIsoDate(os.scheduledDate) : 'Data a definir'}
          </p>
          <p>{os.scheduledPeriod ? SERVICE_ORDER_PERIOD_LABELS[os.scheduledPeriod] : ''}</p>
        </div>
      </header>

      <Block title="Técnico">
        <Row label="Nome" value={os.technician?.name} />
        <Row label="Telefone" value={os.technician?.phone} />
      </Block>

      <Block title="Contato">
        <Row label={capitalize(terms.contact)} value={os.contactName} />
        <Row label="Telefones" value={os.contactPhones} />
      </Block>

      <Block title={capitalize(terms.resource)}>
        <Row label={capitalize(terms.resourceId)} value={os.vehiclePlate} />
        <Row label="Marca/modelo" value={[os.vehicleBrand, os.vehicleModel].filter(Boolean).join(' ')} />
        <Row label="Cor / ano" value={[os.vehicleColor, os.vehicleYear].filter(Boolean).join(' / ')} />
        <Row label="Chassi" value={os.vehicleChassis} />
      </Block>

      <Block title="Endereço">
        <Row
          label="Logradouro"
          value={[
            [os.addressStreet, os.addressNumber].filter(Boolean).join(', '),
            os.addressComplement,
          ]
            .filter(Boolean)
            .join(' — ')}
        />
        <Row label="Bairro" value={os.addressNeighborhood} />
        <Row label="Cidade" value={cityLine} />
        <Row label="CEP" value={os.addressZip} />
      </Block>

      <Block title="Instruções">
        <p className="min-h-16 whitespace-pre-wrap">{os.instructions || '—'}</p>
      </Block>

      <Block title="Execução (preenchido pelo técnico)">
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 pt-2">
          <Blank label="Chegada" />
          <Blank label="Saída" />
          <Blank label="IMEI / equipamento" />
          <Blank label="Chip" />
          <div className="col-span-2">
            <Blank label="Observações" tall />
          </div>
          <Blank label="Assinatura do técnico" />
          <Blank label={`Assinatura do ${terms.contact}`} />
        </div>
      </Block>
    </div>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-4 break-inside-avoid">
      <h2 className="mb-1 border-b border-black/40 text-sm font-bold uppercase">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-3">
      <span className="w-32 shrink-0 font-semibold">{label}</span>
      <span>{value || '—'}</span>
    </div>
  );
}

function Blank({ label, tall }: { label: string; tall?: boolean }) {
  return (
    <div>
      <div className={tall ? 'h-16 border-b border-black' : 'h-6 border-b border-black'} />
      <span className="text-[11px]">{label}</span>
    </div>
  );
}
